/**
 * Attorney Demand Letter Generator
 * 
 * Generates professional demand letters with:
 * - Multiple letter types (initial, follow-up, bad faith notice, final, appraisal)
 * - State-specific statutory references
 * - Auto-populated from case data
 * - Real-time preview
 * - Export options (text, copy)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Send, AlertTriangle, Clock, 
  Gavel, Copy, Download, Check, Loader2, ChevronDown
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface LetterType {
  type: string;
  name: string;
  description: string;
}

const LETTER_TYPES: LetterType[] = [
  { type: 'initial_demand', name: 'Initial Demand Letter', description: 'First formal demand for payment' },
  { type: 'follow_up', name: 'Follow-Up Demand', description: 'Second demand after deadline passes' },
  { type: 'bad_faith_notice', name: 'Bad Faith Notice', description: 'Statutory notice of bad faith conduct' },
  { type: 'final_demand', name: 'Final Demand Before Litigation', description: 'Last chance before lawsuit' },
  { type: 'appraisal_demand', name: 'Appraisal Demand', description: 'Invoke appraisal clause' },
];

// Map frontend letter types to backend template types
const TEMPLATE_TYPE_MAP: Record<string, string> = {
  'initial_demand': 'initial',
  'follow_up': 'followup',
  'bad_faith_notice': 'bad_faith',
  'final_demand': 'final',
  'appraisal_demand': 'appraisal',
};

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

export default function DemandLetterGenerator() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  
  // Form state
  const [letterType, setLetterType] = useState('initial_demand');
  const [responseDeadlineDays, setResponseDeadlineDays] = useState(30);
  const [includeCalculations, setIncludeCalculations] = useState(true);
  const [includeBadFaithWarning, setIncludeBadFaithWarning] = useState(true);
  
  // Case data form
  const [caseData, setCaseData] = useState({
    insured_name: '',
    business_name: '',
    insured_address: '',
    carrier_name: '',
    carrier_address: '',
    policy_number: '',
    claim_number: '',
    adjuster_name: '',
    adjuster_email: '',
    event_type: 'fire',
    event_date: '',
    claim_amount: 0,
    loss_period_days: 30,
    state: '',
    attorney_name: '',
    attorney_firm: '',
    attorney_address: '',
    attorney_phone: '',
    attorney_email: '',
    attorney_bar_number: '',
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingCase, setLoadingCase] = useState(true);
  const [generatedLetter, setGeneratedLetter] = useState<string | null>(null);
  const [reviewSections, setReviewSections] = useState<any[]>([]);
  const [stateStatutes, setStateStatutes] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Load case data on mount
  useEffect(() => {
    const fetchCaseData = async () => {
      if (!caseId) {
        setLoadingCase(false);
        return;
      }

      const token = localStorage.getItem('attorney_token');
      if (!token) {
        navigate('/attorney/login');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.status === 401) {
          localStorage.removeItem('attorney_token');
          navigate('/attorney/login');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          // Pre-fill form with case data
          setCaseData(prev => ({
            ...prev,
            insured_name: data.client_name || '',
            business_name: data.business_name || data.client_name || '',
            carrier_name: data.carrier || '',
            claim_number: data.claim_number || '',
            policy_number: data.policy_number || '',
            claim_amount: data.total_demand || 0,
            event_type: data.event_type || 'fire',
            event_date: data.loss_date || '',
            state: data.state || 'TX',
          }));
        }
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoadingCase(false);
      }
    };

    fetchCaseData();
  }, [caseId, navigate]);

  // Fetch case context for additional auto-population
  useEffect(() => {
    if (!caseId) return;
    const token = localStorage.getItem('attorney_token');
    fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/context`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setCaseData(prev => ({
            ...prev,
            // Only set from context if the field is still at its default value
            event_date: prev.event_date || data.event_date || '',
            claim_number: prev.claim_number || data.claim_number || '',
            carrier_name: prev.carrier_name || data.carrier || '',
            insured_name: prev.insured_name || data.client_name || '',
            business_name: prev.business_name || data.business_name || '',
            claim_amount: prev.claim_amount || data.claim_amount || 0,
          }));
        }
      })
      .catch(err => console.error('Failed to load case context:', err));
  }, [caseId]);
  
  // Load state statutes when state changes
  useEffect(() => {
    const fetchStateStatutes = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/state-statutes/${caseData.state}`);
        if (response.ok) {
          const data = await response.json();
          setStateStatutes(data.statutes);
        }
      } catch (err) {
        console.error('Failed to load state statutes:', err);
      }
    };
    
    if (caseData.state) {
      fetchStateStatutes();
    }
  }, [caseData.state]);
  
  const handleInputChange = (field: string, value: string | number) => {
    setCaseData(prev => ({ ...prev, [field]: value }));
  };
  
  const generateLetter = async () => {
    setLoading(true);
    setError('');
    setGeneratedLetter(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to generate letter');
      }
      
      const data = await response.json();
      setGeneratedLetter(data.formatted_letter);
      setReviewSections(data.review_sections || []);
      setStateStatutes(data.state_statutes);
    } catch (err: any) {
      setError(err.message || 'Failed to generate letter');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = useCallback(() => {
    if (generatedLetter) {
      navigator.clipboard.writeText(generatedLetter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [generatedLetter]);
  
  const downloadAsText = useCallback(() => {
    if (generatedLetter) {
      const blob = new Blob([generatedLetter], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [generatedLetter, caseData.claim_number]);

  const downloadAsPdf = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      setError('Failed to download PDF');
    }
  }, [letterType, caseData, includeCalculations, includeBadFaithWarning, responseDeadlineDays]);

  const downloadAsDocx = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/docx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate DOCX');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('DOCX download failed:', err);
      setError('Failed to download DOCX');
    }
  }, [letterType, caseData, includeCalculations, includeBadFaithWarning, responseDeadlineDays]);

  const downloadTemplateDocx = useCallback(async () => {
    if (!caseId) {
      setError('Case ID required for template download');
      return;
    }
    try {
      const templateType = TEMPLATE_TYPE_MAP[letterType] || letterType;
      const response = await fetch(`${BACKEND_URL}/api/v1/reports/${caseId}/demand-letter/${templateType}`);

      if (!response.ok) throw new Error('Failed to generate Template DOCX');

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `demand_letter_template_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.docx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template DOCX download failed:', err);
      setError('Failed to download Template DOCX');
    }
  }, [caseId, letterType, caseData.claim_number]);

  const getLetterTypeIcon = (type: string) => {
    switch (type) {
      case 'initial_demand': return <FileText className="w-5 h-5" />;
      case 'follow_up': return <Send className="w-5 h-5" />;
      case 'bad_faith_notice': return <AlertTriangle className="w-5 h-5" />;
      case 'final_demand': return <Gavel className="w-5 h-5" />;
      case 'appraisal_demand': return <Clock className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  if (loadingCase) {
    return (
      <AttorneyLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout>
      {/* Back button */}
      {caseId && (
        <button
          onClick={() => navigate(`/attorney/case/${caseId}`)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
          data-testid="back-to-case"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Case
        </button>
      )}
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <FileText className="w-7 h-7 text-blue-600" />
          Demand Letter Generator
        </h1>
        <p className="text-slate-500 mt-1">Generate professional demand letters with state-specific statutory references</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Form */}
        <div className="space-y-6">
          {/* Letter Type Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Letter Type</h3>
            <div className="space-y-2">
              {LETTER_TYPES.map((lt) => (
                <label
                  key={lt.type}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    letterType === lt.type 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`letter-type-${lt.type}`}
                >
                  <input
                    type="radio"
                    name="letterType"
                    value={lt.type}
                    checked={letterType === lt.type}
                    onChange={(e) => setLetterType(e.target.value)}
                    className="sr-only"
                  />
                  <div className={letterType === lt.type ? 'text-blue-600' : 'text-slate-400'}>
                    {getLetterTypeIcon(lt.type)}
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{lt.name}</div>
                    <div className="text-xs text-slate-500">{lt.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Case Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Case Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Insured Name *</label>
                <input
                  type="text"
                  value={caseData.insured_name}
                  onChange={(e) => handleInputChange('insured_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Smith"
                  data-testid="input-insured-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={caseData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ABC Restaurant LLC"
                  data-testid="input-business-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Carrier Name *</label>
                <input
                  type="text"
                  value={caseData.carrier_name}
                  onChange={(e) => handleInputChange('carrier_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="State Farm Insurance"
                  data-testid="input-carrier-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State *</label>
                <select
                  value={caseData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="select-state"
                >
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Policy Number *</label>
                <input
                  type="text"
                  value={caseData.policy_number}
                  onChange={(e) => handleInputChange('policy_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="POL-123456"
                  data-testid="input-policy-number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Claim Number *</label>
                <input
                  type="text"
                  value={caseData.claim_number}
                  onChange={(e) => handleInputChange('claim_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="CLM-789012"
                  data-testid="input-claim-number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Claim Amount ($) *</label>
                <input
                  type="number"
                  value={caseData.claim_amount}
                  onChange={(e) => handleInputChange('claim_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="500000"
                  data-testid="input-claim-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Event Date</label>
                <input
                  type="date"
                  value={caseData.event_date}
                  onChange={(e) => handleInputChange('event_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="input-event-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Event Type</label>
                <select
                  value={caseData.event_type}
                  onChange={(e) => handleInputChange('event_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="select-event-type"
                >
                  <option value="fire">Fire</option>
                  <option value="flood">Flood</option>
                  <option value="hurricane">Hurricane</option>
                  <option value="tornado">Tornado</option>
                  <option value="theft">Theft</option>
                  <option value="vandalism">Vandalism</option>
                  <option value="water_damage">Water Damage</option>
                  <option value="wind">Wind</option>
                  <option value="hail">Hail</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Response Deadline (Days)</label>
                <input
                  type="number"
                  value={responseDeadlineDays}
                  onChange={(e) => setResponseDeadlineDays(parseInt(e.target.value) || 30)}
                  min={7}
                  max={90}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="input-deadline-days"
                />
              </div>
            </div>

            {/* Advanced Fields Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 mt-4 text-sm text-blue-600 hover:text-blue-700"
              data-testid="toggle-advanced"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Fields
            </button>

            {showAdvanced && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Attorney Name</label>
                  <input
                    type="text"
                    value={caseData.attorney_name}
                    onChange={(e) => handleInputChange('attorney_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Jane Doe, Esq."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Law Firm</label>
                  <input
                    type="text"
                    value={caseData.attorney_firm}
                    onChange={(e) => handleInputChange('attorney_firm', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Smith & Associates"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Adjuster Name</label>
                  <input
                    type="text"
                    value={caseData.adjuster_name}
                    onChange={(e) => handleInputChange('adjuster_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Claims Adjuster"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Loss Period (Days)</label>
                  <input
                    type="number"
                    value={caseData.loss_period_days}
                    onChange={(e) => handleInputChange('loss_period_days', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="90"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Letter Options</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeCalculations}
                  onChange={(e) => setIncludeCalculations(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Include loss calculations section</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeBadFaithWarning}
                  onChange={(e) => setIncludeBadFaithWarning(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Include bad faith warning</span>
              </label>
            </div>
          </div>

          {/* State Statutes Info */}
          {stateStatutes && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
              <h4 className="font-medium text-amber-900 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" />
                {caseData.state} Statutory References
              </h4>
              <div className="text-sm text-amber-800 space-y-1">
                <p><strong>Unfair Practices:</strong> {stateStatutes.unfair_practices}</p>
                <p><strong>Bad Faith:</strong> {stateStatutes.bad_faith}</p>
                <p><strong>Interest Rate:</strong> {stateStatutes.interest_rate}</p>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={generateLetter}
            disabled={loading || !caseData.insured_name || !caseData.carrier_name}
            className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="generate-letter-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                Generate Letter
              </>
            )}
          </button>
          
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Right Column: Preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-slate-900">Letter Preview</h3>
              {generatedLetter && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="copy-letter-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadAsText}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="download-txt-btn"
                  >
                    <Download className="w-4 h-4" />
                    TXT
                  </button>
                  <button
                    onClick={downloadAsPdf}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  <button
                    onClick={downloadAsDocx}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100"
                    data-testid="download-docx-btn"
                  >
                    <Download className="w-4 h-4" />
                    DOCX
                  </button>
                  {caseId && (
                    <button
                      onClick={downloadTemplateDocx}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100"
                      data-testid="download-template-docx-btn"
                      title="Download from pre-built DOCX template"
                    >
                      <Download className="w-4 h-4" />
                      Template
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 max-h-[700px] overflow-y-auto">
              {generatedLetter ? (
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-800 leading-relaxed" data-testid="letter-preview">
                  {generatedLetter}
                </pre>
              ) : (
                <div className="text-center py-16 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Fill in the form and click "Generate Letter" to see the preview</p>
                </div>
              )}
            </div>
          </div>

          {/* Review Sections Alert */}
          {reviewSections.length > 0 && (
            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
              <h4 className="font-medium text-yellow-900 flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5" />
                Attorney Review Required ({reviewSections.length} sections)
              </h4>
              <ul className="space-y-2">
                {reviewSections.map((section, idx) => (
                  <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span>
                      <strong>{section.location || section.reason}</strong>
                      {section.priority && <span className="ml-2 text-xs bg-yellow-200 px-1.5 py-0.5 rounded">{section.priority}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </AttorneyLayout>
  );
}
